import { SBOM_PORTAL } from '../../../../shared/featureFlags/flagNames';
import { Action, ResourceType } from '../../../../typings/permissions';
import { useParams, useQuery } from '../../../contexts/RouterContext';
import { useIsFeatureEnabled } from '../../../helpers/features/featureFlags';
import useHasPermission from '../../../shared/permissions/useHasPermission';
import { isReleaseGroupScope } from '../types';
import type { ReportsV3Params, ReportsV3QueryParams } from '../types';

import { useReportType } from './useReportType';

/**
 * Whether the current user can publish the current report to the SBOM portal.
 *
 * Publishing is only available for SBOM reports at the release group level, and
 * requires both the `SBOM_PORTAL` feature flag and the "manage organization
 * portal reports" permission. SPDX 3.0 cannot be published: the portal has no
 * publication status for it.
 */
export function useCanPublishSbom(): boolean {
  const reportType = useReportType();
  const params = useParams<ReportsV3Params>();
  const [query] = useQuery<ReportsV3QueryParams>();
  const isReleaseGroup = isReleaseGroupScope(params);
  const isSbomPortalEnabled = useIsFeatureEnabled(SBOM_PORTAL);
  const hasPermission = useHasPermission();
  const canManagePortalReports = hasPermission({
    resourceType: ResourceType.OrganizationPortalReports,
    action: Action.Manage,
  });

  return (
    reportType === 'sbom' &&
    query.format !== 'SPDX3_JSON' &&
    isReleaseGroup &&
    isSbomPortalEnabled &&
    canManagePortalReports
  );
}
